import React, { useMemo } from 'react';
import { SimpleGrid, Stack } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import KpiPage, {
  ChartCard,
  ChartTooltip,
  QueryState,
  StatTile,
  fmtMoney,
  fmtPct,
  useChartColors,
} from '../KpiPage';
import { kpiBySlug } from '../kpiCatalog';
import { useWindowParams } from '../DateWindowSelector';
import useNexusQuery, { field, num } from '../../../hooks/useNexusQuery';

const kpi = kpiBySlug('margin-by-inventory-bucket');

// The query returns qualified bucket names — "Purchasing (supply chain stock)", "Sourcing
// (buy-to-order)" — because on the scorecard table those two get conflated without the
// qualifier. On a chart axis there is no room for it, and the bars are adjacent so the contrast
// is visible anyway, so strip the parenthetical here only.
const shortBucket = (b) => String(b ?? '').replace(/\s*\(.*\)\s*$/, '');

export default function MarginByInventoryBucket() {
  const c = useChartColors();
  const [window, setWindow] = useWindowParams();

  const buckets = useNexusQuery('initiative-margin-by-bucket', window);

  const data = useMemo(
    () =>
      buckets.rows.map((r) => ({
        bucket: shortBucket(field(r, 'Bucket')),
        fullBucket: field(r, 'Bucket'),
        revenue: num(r, 'Revenue'),
        marginPct: num(r, 'GrossMarginPct'),
        margin: num(r, 'GrossMargin'),
      })),
    [buckets.rows]
  );

  const summary = useMemo(() => {
    if (!data.length) return null;
    const ranked = [...data].sort((a, b) => b.marginPct - a.marginPct);
    const totalRevenue = data.reduce((s, d) => s + d.revenue, 0);
    const totalMargin = data.reduce((s, d) => s + d.margin, 0);
    return {
      best: ranked[0],
      worst: ranked[ranked.length - 1],
      spread: ranked[0].marginPct - ranked[ranked.length - 1].marginPct,
      blended: totalRevenue ? (totalMargin / totalRevenue) * 100 : 0,
    };
  }, [data]);

  // Color each bar against the blended company rate — the whole point of the KPI is that a
  // single blended number is the wrong yardstick, so showing it as the reference line makes
  // the argument visually.
  const barColor = (marginPct) =>
    summary && marginPct >= summary.blended ? c.positive : c.negative;

  return (
    <KpiPage kpi={kpi} window={window} onWindowChange={setWindow} hideMethodology>
      <Stack gap="md">
        <QueryState isLoading={buckets.isLoading} error={buckets.error} isEmpty={!data.length}>
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            <StatTile
              label="Blended margin rate"
              value={fmtPct(summary?.blended ?? 0)}
              sub="The number per-bucket targets should replace"
            />
            <StatTile
              label="Best bucket"
              value={fmtPct(summary?.best?.marginPct ?? 0)}
              sub={summary?.best?.bucket ?? ''}
              color={c.positive}
            />
            <StatTile
              label="Worst bucket"
              value={fmtPct(summary?.worst?.marginPct ?? 0)}
              sub={summary?.worst?.bucket ?? ''}
              color={c.negative}
            />
            <StatTile
              label="Spread"
              value={`${(summary?.spread ?? 0).toFixed(1)} pts`}
              sub="Why one company-wide target cannot work"
              color={c.series[3]}
            />
          </SimpleGrid>
        </QueryState>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <ChartCard
            title="Margin rate by bucket"
            caption="Bars above the blended company rate are green, below are red. The spread is the argument: holding every bucket to one number would let some coast and punish others for economics they do not control."
          >
            <QueryState isLoading={buckets.isLoading} error={buckets.error} isEmpty={!data.length}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                  <XAxis dataKey="bucket" stroke={c.axis} fontSize={11} />
                  <YAxis
                    stroke={c.axis}
                    fontSize={12}
                    tickFormatter={(v) => `${Math.round(v)}%`}
                    width={48}
                  />
                  <Tooltip
                    content={<ChartTooltip formatter={(v) => fmtPct(v)} />}
                    cursor={{ fill: c.grid, opacity: 0.3 }}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Bar dataKey="marginPct" name="Gross margin rate" radius={[4, 4, 0, 0]}>
                    {data.map((d) => (
                      <Cell key={d.bucket} fill={barColor(d.marginPct)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>

          <ChartCard
            title="Revenue mix by bucket"
            caption="Margin rate alone misleads — the best-earning bucket may sit on the smallest base. Weighting by revenue shows where an improvement goal is actually worth setting."
          >
            <QueryState isLoading={buckets.isLoading} error={buckets.error} isEmpty={!data.length}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  layout="vertical"
                  margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={c.grid} horizontal={false} />
                  <XAxis
                    type="number"
                    stroke={c.axis}
                    fontSize={12}
                    tickFormatter={(v) => fmtMoney(v)}
                  />
                  <YAxis
                    type="category"
                    dataKey="bucket"
                    stroke={c.axis}
                    fontSize={11}
                    width={92}
                  />
                  <Tooltip
                    content={<ChartTooltip formatter={fmtMoney} />}
                    cursor={{ fill: c.grid, opacity: 0.3 }}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Bar dataKey="revenue" name="Revenue" fill={c.series[0]} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>
        </SimpleGrid>
      </Stack>
    </KpiPage>
  );
}
